/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable use-encapsulation/prefer-custom-hooks */
import { SectionLoader } from '@/components/shared';
import type { IAddress, IAddressBook, IBaseForm, ILatLng } from '@/types';
import { hasEmptyFields, hasFormAnyError } from '@/utils';
import { Button, Flex, Heading, Radio, RadioGroup, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ERRORS } from '../../constants';
import type { IShippingFormFields } from '../../types';
import { AddressFields, AlertDialogInfo, MbeInput } from '../shared';
import { DeliveryAddressSelector } from './DeliveryAddressSelector';
import { ExtraServices } from './ExtraServices';
import { StoreSelector } from './StoreSelector';

const requiredFields = [
  'recipient',
  'streetAddress',
  'city',
  'countryCode',
  'province',
  'zipCode',
  'store',
];

interface IShippinFormProps extends IBaseForm<IShippingFormFields> {
  addressBook?: IAddressBook[];
  initialMapCenter?: ILatLng;
  isLoading: boolean;
  isLogged: boolean;
  isStoreSelectorDisabled?: boolean;
  onCloseAddressModal: () => void;
  userAddress?: IAddress;
  userName?: string;
}

export const ShippingForm: React.FC<IShippinFormProps> = ({
  addressBook,
  defaultValues,
  initialMapCenter,
  isBackVisible = true,
  isLoading,
  isLogged,
  isStoreSelectorDisabled = false,
  onBack,
  onCloseAddressModal,
  onSubmit: providedOnSubmit,
  userAddress,
  userName,
}): JSX.Element => {
  const methods = useForm<IShippingFormFields>({
    mode: 'onBlur',
    defaultValues: { isPrivateHome: true, ...defaultValues },
  });

  const [shouldOpenDialog, setShouldOpenDialog] = useState<boolean>(false);

  const {
    control,
    formState: { errors },
    handleSubmit: submitHandler,
    register,
    resetField,
    watch,
  } = methods;

  const { t } = useTranslation();

  const { field: myCenterField } = useController({
    name: 'store',
    control,
    rules: {
      required: { value: true, message: t(ERRORS.empty) },
    },
  });

  const { field: isPrivateHomeField } = useController({
    name: 'isPrivateHome',
    control,
  });

  const selectedStore = watch('store');
  const isPrivateHome = Boolean(watch('isPrivateHome'));
  const hasAddressBook = addressBook && addressBook.length > 0;

  const areRequiredFieldsEmpty = hasEmptyFields<
    IShippingFormFields,
    Extract<keyof IShippingFormFields, string>
  >(watch(), requiredFields as unknown as Extract<keyof IShippingFormFields, string>[]);

  const isDisabled = hasAddressBook
    ? hasFormAnyError(errors) || hasEmptyFields(watch(), ['store'])
    : areRequiredFieldsEmpty || hasFormAnyError(errors);

  const handleSubmit: SubmitHandler<IShippingFormFields> = (data, event): void => {
    event?.preventDefault();

    const shippingData: IShippingFormFields = data.deliveryAddress
      ? {
          recipient: data.deliveryAddress.name,
          extraServices: data.extraServices,
          store: data.store,
          isPrivateHome: data.deliveryAddress.isPrivateHome,
          notes: data.notes,
          province: data.deliveryAddress.province,
          zipCode: data.deliveryAddress.zipCode,
          streetAddress: data.deliveryAddress.streetAddress,
          city: data.deliveryAddress.city,
          countryCode: data.deliveryAddress.countryCode,
        }
      : data;

    if (!data.extraServices.isInsuranceRequested && !shouldOpenDialog) {
      setShouldOpenDialog(true);
      return;
    }

    providedOnSubmit?.(shippingData);
  };

  const handleBack = (): void => {
    onBack?.();
  };

  const toggleDialog = (open: boolean): void => {
    setShouldOpenDialog(open);
  };

  const shouldShowDeliveryAddressBook =
    addressBook && addressBook.length > 0 && userAddress && userName && !isLoading;

  const isNotLogged = !isLogged || addressBook?.length === 0;

  useEffect(() => {
    const {
      city = '',
      countryCode = '',
      province = '',
      recipient = '',
      streetAddress = '',
      zipCode = '',
    } = defaultValues ?? {};

    resetField('recipient', { defaultValue: recipient });
    resetField('streetAddress', { defaultValue: streetAddress });
    resetField('city', { defaultValue: city });
    resetField('province', { defaultValue: province });
    resetField('countryCode', { defaultValue: countryCode });
    resetField('zipCode', { defaultValue: zipCode });
  }, [defaultValues, resetField]);

  return (
    <FormProvider {...methods}>
      <form id="shippingForm" name="shippingForm" onSubmit={submitHandler(handleSubmit)}>
        {shouldShowDeliveryAddressBook && (
          <DeliveryAddressSelector
            addressBook={addressBook}
            userAddress={userAddress}
            userName={userName}
            onClose={(): void => {
              onCloseAddressModal();
            }}
          />
        )}
        {isLoading && <SectionLoader />}
        <Flex direction="column" gap={12} mb={8}>
          {isNotLogged && (
            <>
              <MbeInput
                error={errors.recipient}
                id="recipient"
                placeholder={t('FORM.RECIPIENT')}
                {...register('recipient', {
                  required: { value: true, message: t(ERRORS.empty) },
                })}
              />
              <AddressFields />

              <RadioGroup
                name="isPrivateHome"
                value={isPrivateHome.toString()}
                onChange={(nextVal): void => {
                  isPrivateHomeField.onChange(Boolean(nextVal === 'true'));
                }}
              >
                <Flex gap={8}>
                  <Radio spacing={4} value="true" variant="mbe">
                    {t('FORM.ADDRESS_TYPE_HOME')}
                  </Radio>
                  <Radio spacing={4} value="false" variant="mbe">
                    {t('FORM.ADDRESS_TYPE_BUSINESS')}
                  </Radio>
                </Flex>
              </RadioGroup>
            </>
          )}
          <MbeInput id="notes" placeholder={t('FORM.DELIVERY_NOTES')} {...register('notes')} />

          {!isStoreSelectorDisabled && (
            <Flex flexDirection="column" gap={4}>
              <Heading fontSize="large" fontWeight="bold">
                {t('FORM.STORE_SELECTION_TITLE')}
              </Heading>
              <Text>{t('FORM.STORE_SELECTION_DESC')}</Text>
              {initialMapCenter && (
                <StoreSelector
                  initialCenter={initialMapCenter}
                  store={selectedStore}
                  onSelect={myCenterField.onChange}
                />
              )}
            </Flex>
          )}
          <ExtraServices />
        </Flex>
        <Flex flexDirection="row-reverse" gap={2} justifyContent="space-between">
          <Button disabled={isDisabled} type="submit" variant="mbePrimary">
            {t('ACTIONS.NEXT')}
          </Button>
          {isBackVisible && (
            <Button color="brand.red.light" variant="link" onClick={handleBack}>
              {t('ACTIONS.PREV')}
            </Button>
          )}
        </Flex>
        <AlertDialogInfo
          shouldOpenDialog={shouldOpenDialog}
          onSubmit={submitHandler(handleSubmit)}
          onToggleDialog={toggleDialog}
        ></AlertDialogInfo>
      </form>
    </FormProvider>
  );
};
